import { useState } from 'react'
import { Pressable, StyleSheet, Text, TextInput, View } from 'react-native'
import { formatGBP } from '../format'
import { colors, fontFamily } from '../tokens'
import { trpc } from '../trpc'
import { ErrorNotice } from './ErrorNotice'

type Props = {
  bandCheckId: string
  currentBand: string
  currentValueCapGbp: number
  onAgreed: () => void
}

const parsePounds = (raw: string): number | null => {
  const cleaned = raw.replace(/[£,\s]/g, '')
  if (!cleaned) return null
  const n = Number(cleaned)
  if (!Number.isFinite(n) || n <= 0) return null
  return Math.round(n)
}

export const SecondChancePrompt = ({
  bandCheckId,
  currentBand,
  currentValueCapGbp,
  onAgreed,
}: Props) => {
  const [valueText, setValueText] = useState('')
  const submit = trpc.bandCheck.secondChance.useMutation({
    onSuccess: (data) => {
      if (data.agreed) onAgreed()
    },
  })

  const parsed = parsePounds(valueText)
  const inputValid = parsed != null
  const hasResult = submit.isSuccess
  const result = submit.data

  if (hasResult && result?.agreed) {
    return (
      <View style={[styles.box, styles.boxAgreed]}>
        <Text style={styles.title}>Second-chance check passed</Text>
        <Text style={styles.subtitle}>
          Your valuation ({formatGBP(result.userValueGbp)}) was independently corroborated (
          {formatGBP(result.avmValueGbp)}). Your case has been re-scored and now flags as a
          potential overband — see the appeal options below.
        </Text>
      </View>
    )
  }

  if (hasResult && result && !result.agreed) {
    return (
      <View style={[styles.box, styles.boxRejected]}>
        <Text style={styles.title}>Second-chance check didn't pass</Text>
        <Text style={styles.subtitle}>
          {result.rejectionDetail ??
            "We couldn't confirm enough valuation evidence to support an appeal at this time."}
        </Text>
        <Text style={styles.fineprint}>
          Filing without supporting valuation evidence carries a real risk of the Valuation Office
          leaving your band unchanged — or in rare cases, increasing it. We'd rather not charge you
          for a case we don't believe will succeed.
        </Text>
      </View>
    )
  }

  return (
    <View style={styles.box}>
      <Text style={styles.title}>Have a current valuation we can check?</Text>
      <Text style={styles.subtitle}>
        We found some structural evidence your Band {currentBand} might be wrong, but not enough
        valuation data to make a confident case. If your property is currently worth less than{' '}
        <Text style={styles.bold}>{formatGBP(currentValueCapGbp)}</Text>, a band reduction may be
        possible. Enter your honest estimate (Zoopla, Rightmove, or a recent agent appraisal) and
        we'll cross-check it with an independent valuation before deciding whether to proceed.
      </Text>
      <Text style={styles.fineprint}>
        We'll only proceed if both your estimate AND our independent check support the case. If
        either suggests your band is correct, we won't charge you.
      </Text>
      <TextInput
        accessibilityLabel="Current property value in pounds"
        editable={!submit.isPending}
        keyboardType="numeric"
        onChangeText={setValueText}
        placeholder="e.g. 250000"
        style={styles.input}
        value={valueText}
      />
      <Pressable
        accessibilityRole="button"
        disabled={!inputValid || submit.isPending}
        onPress={() => {
          if (parsed != null) submit.mutate({ bandCheckId, userValueGbp: parsed })
        }}
        style={[styles.button, (!inputValid || submit.isPending) && styles.buttonDisabled]}
      >
        <Text style={styles.buttonText}>
          {submit.isPending ? 'Checking…' : 'Cross-check this valuation'}
        </Text>
      </Pressable>
      <ErrorNotice error={submit.error} />
    </View>
  )
}

const styles = StyleSheet.create({
  box: {
    marginTop: 12,
    padding: 16,
    borderRadius: 10,
    backgroundColor: colors.primary[100],
    borderWidth: 1,
    borderColor: colors.primary[500],
    gap: 8,
  },
  boxAgreed: { backgroundColor: colors.positiveSoft, borderColor: colors.positive },
  boxRejected: { backgroundColor: colors.error[100], borderColor: colors.error[700] },
  title: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.primary[900],
    fontFamily: fontFamily.body,
  },
  subtitle: {
    fontSize: 13,
    color: colors.primary[700],
    lineHeight: 18,
    fontFamily: fontFamily.body,
  },
  fineprint: { fontSize: 12, color: colors.muted, marginTop: 4, fontFamily: fontFamily.body },
  bold: { fontWeight: '700' },
  input: {
    marginTop: 4,
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.borderStrong,
    backgroundColor: colors.surface,
    fontSize: 16,
    fontFamily: fontFamily.body,
  },
  button: {
    marginTop: 4,
    backgroundColor: colors.primary[700],
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  buttonDisabled: { opacity: 0.5 },
  buttonText: {
    color: colors.surface,
    fontSize: 15,
    fontWeight: '600',
    fontFamily: fontFamily.body,
  },
  error: { color: colors.error[700], fontSize: 13, marginTop: 4, fontFamily: fontFamily.body },
})
